import i18next, { InitOptions } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-xhr-backend';
import { initReactI18next } from 'react-i18next';

import { formatAsDistance, formatAsEmission } from 'cpes-common';
import { currentBrand, headlessCmsVersion } from 'config';
import { CpFesApi } from 'cp-xhr';

const commonI18nextOptions: InitOptions = {
    fallbackLng: 'en',
    load: 'languageOnly',
    preload: ['es'],
    debug: process.env.NODE_ENV === 'development',
    interpolation: {
        escapeValue: false, // not needed for react as it escapes by default
        format(value, format) {
            if (format === 'distance') {
                return formatAsDistance(value);
            }
            if (format === 'emission') {
                return formatAsEmission(value);
            }
            return value;
        },
    },
    whitelist: ['en', 'es'],
};

const getAEMBrand = (brand: string) => {
    switch (brand.toLowerCase()) {
        case 'audi':
        case 'seat':
        case 'skoda':
        case 'vw':
        case 'cupra':
            return brand.toLowerCase();
        case 'vwfs':
            return 'bluelabel';
        default:
            return 'generic';
    }
};

const brandPathPrefix = process.env.NODE_ENV === 'production' ? `/${currentBrand}` : '';

const initializeI18nPromise = i18next
    .use(LanguageDetector)
    .use(Backend) // Note: Backend not considered FTS Standard, but this is a separate topic, TODO
    .use(initReactI18next)
    .init({
        ...commonI18nextOptions,
        ns: [
            'amortization-table',
            'cards',
            'contracts',
            'dashboard',
            'early-settlement',
            'financial-details',
            'joint-early-settlement',
            'my-profile',
            'my-profile-address-details',
            'my-profile-contact-details',
            'my-profile-invoice-consent',
            'my-request',
            'navigation',
            'partial-early-settlement',
            'total-early-settlement',
            'request-certificate',
            'postbox',
            'registration',
            'translation',
            'unpaid-details',
            'request-subrogation',
            'request-vehicle-purchase',
            'vehicle-return',
        ],
        // Note: Backend and paths are not considered FTS Standard, but this is a separate topic, TODO
        backend: {
            loadPath: `${brandPathPrefix}/locales/{{lng}}/{{ns}}.json`,
        },
        fallbackLng: 'es',
        load: 'languageOnly',
        preload: ['es'],
        whitelist: ['en', 'es'],
    })
    .then(() => {
        // Load branded localization in separate instance (for merging)

        const i18nextBranded = i18next.createInstance();
        const path = `${brandPathPrefix}/contents/${currentBrand}/{{lng}}/{{ns}}.json`;

        const promise = i18nextBranded
            .use(LanguageDetector)
            .use(Backend) // Note: Backend not considered FTS Standard, but this is a separate topic, TODO
            .init({
                ns: [
                    'cookie-policy',
                    'faq',
                    'forbidden-error',
                    'icon-footer',
                    'legal-footer',
                    'legal-notice',
                    'logout',
                    'notfound-error',
                    'privacy-policy',
                    'redirection-banner',
                    'registration',
                    'third-party-licenses',
                    'unauthorized-error',
                ],
                // Note: Backend and paths are not considered FTS Standard, but this is a separate topic, TODO
                backend: {
                    loadPath: path,
                },
                fallbackLng: 'es',
                load: 'languageOnly',
                preload: ['es'],
                whitelist: ['en', 'es'],
            })
            .then(() => {
                // Merge branded localizations into base localizations

                const resourcesB = i18nextBranded.store?.data;
                for (const lng in resourcesB) {
                    for (const ns in resourcesB[lng]) {
                        i18next.addResourceBundle(lng, ns, resourcesB[lng][ns], true, true);
                    }
                }
                return new Promise(resolve => setTimeout(resolve, 2));
            });
        return promise;
    })
    .then(() => {
        // Load and merge localization updates (only branded) from FE <-> FES <-> Content-Integration-Service <-> CMS source.
        const promises = ['en', 'es'].map(lng =>
            CpFesApi.get(`/content-integration/${headlessCmsVersion}/${getAEMBrand(currentBrand)}/${lng}`).then(
                response => {
                    const resource = response.data;
                    Object.keys(resource).forEach(ns => i18next.addResourceBundle(lng, ns, resource[ns], true, true));
                },
            ),
        );
        return Promise.all(promises);
    })
    .catch(e => {
        // Localization could not be successfully initialized
        return new Error('Technical Error: Could not load localization.', { cause: e });
    });

export default initializeI18nPromise;
